import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, RingProgress, Tooltip } from '@mantine/core';
import { LineChart, BarChart } from '@mantine/charts';
import { useApiClient } from '../../api/ApiClientContext';
import { InitiativeInsightsResponse, ProgrammeResponse } from '../../api/Api';
import { Icon } from '../../assets/icons/Icon';
import { StreamDetailsModal } from './StreamDetailsModal';
import styles from './InitiativeInsights.module.scss';

const STAT_CARDS_META = [
  {
    key: 'engagement_rate' as const,
    label: 'ENGAGEMENT RATE',
    format: (v: number) => `${v} avg. days logged`,
  },
  {
    key: 'consistency_rate' as const,
    label: 'CONSISTENCY RATE MET (2+ ACTIVE D/W)',
    format: (v: number) => `${v}%`,
  },
  {
    key: 'active_users_pct' as const,
    label: 'ACTIVE USERS',
    format: (v: number) => `${v}%`,
  },
  {
    key: 'users_endorsed_pct' as const,
    label: 'USERS ENDORSED',
    format: (v: number) => `${v}%`,
  },
];

const InitiativeInsights: React.FC = () => {
  const apiClient = useApiClient();
  const [programmeId, setProgrammeId] = useState<string | null>(null);
  const [streamModal, setStreamModal] = useState<{ programmeId: string; streamId: string } | null>(null);

  const { data: programmes } = useQuery<ProgrammeResponse[]>({
    queryKey: ['programmes'],
    queryFn: async () => {
      const res = await apiClient.api.getAvailableProgrammesApiV1ProgrammesGet();
      return res?.data;
    },
  });

  useEffect(() => {
    if (programmes?.length && !programmeId) {
      setProgrammeId(programmes[0].id);
    }
  }, [programmes, programmeId]);

  const { data, isLoading } = useQuery<InitiativeInsightsResponse>({
    queryKey: ['initiativeInsights', programmeId],
    queryFn: async () => {
      const res = await apiClient.api.getInitiativeInsightsApiV1InsightsInitiativeProgrammeIdGet(programmeId!);
      return res?.data;
    },
    enabled: !!programmeId,
  });

  const enabled = data?.enabled_components ?? [];
  const has = useCallback((c: string) => enabled.includes(c), [enabled]);

  const accessibleProgrammes = data?.accessible_programmes ?? [];
  const programmeOptions = accessibleProgrammes.map((p) => ({ value: p.id, label: p.name }));

  const top_positive_impacts = data?.top_positive_impacts;
  const impactsData = top_positive_impacts?.map((item) => ({
    name: item.name.length > 12 ? item.name.slice(0, 12) + '...' : item.name,
    count: item.count,
    fullName: item.name,
  })) ?? [];

  const ImpactYAxisTick = useCallback(({ x, y, payload }: any) => {
    const item = impactsData.find((d: any) => d.name === payload.value);
    const fullName = item?.fullName || payload.value;
    const isTruncated = fullName !== payload.value;

    return (
      <Tooltip label={fullName} disabled={!isTruncated} position="left" withArrow>
        <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="#495057" style={{ cursor: isTruncated ? 'pointer' : 'default' }}>
          {payload.value}
        </text>
      </Tooltip>
    );
  }, [impactsData]);

  if (isLoading) {
    return <div className={styles.loading}>Loading initiative insights...</div>;
  }

  if (!data || (data.enabled_components?.length === 0 && !data.counts && !data.streams?.length)) {
    return (
      <div className={styles.root}>
        <div className={styles.titleRow}>
          <h1 className={styles.pageTitle}>Initiative insights</h1>
        </div>
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrapper}>
            <Icon iconName="GreyCalendarIcon" width="5rem" height="5rem" />
          </div>
          <h3 className={styles.emptyTitle}>No active initiative</h3>
          <p className={styles.emptyText}>
            There are no initiative insights available at the moment. Data will appear once an initiative is active.
          </p>
        </div>
      </div>
    );
  }

  const { counts, stats, activity_over_time, distribution, streams } = data;

  const activityChartData = activity_over_time?.weeks.map((w, i) => ({
    week: `W${i + 1}`,
    avg_days_logged: w.avg_days_logged,
    average: activity_over_time.all_time_average,
  })) ?? [];

  const distributionData = distribution?.map((d) => ({
    bucket: d.bucket,
    percentage: d.percentage,
  })) ?? [];

  const showStreamChevron = has('init_stream_modal');

  return (
    <div className={styles.root}>
      {/* Title Row */}
      <div className={styles.titleRow}>
        <h1 className={styles.pageTitle}>Initiative insights</h1>
        {accessibleProgrammes.length > 1 ? (
          <Select
            data={programmeOptions}
            value={programmeId}
            onChange={(val) => val && setProgrammeId(val)}
            rightSection={<Icon iconName="ArrowDownIcon" width="0.75rem" height="0.75rem" />}
            classNames={{
              input: styles.selectInput,
              root: styles.selectRoot,
            }}
            allowDeselect={false}
          />
        ) : (
          <span className={styles.programmeName}>{data.programme_name}</span>
        )}
      </div>

      {/* Counts Bar */}
      {has('init_counts') && counts && (
        <div className={styles.countsBar}>
          <div className={styles.countItem}>
            <span className={styles.countLabel}>Streams</span>
            <span className={styles.countValue}>{counts.streams}</span>
          </div>
          <div className={styles.countItem}>
            <span className={styles.countLabel}>Active cohorts</span>
            <span className={styles.countValue}>{counts.active_cohorts}</span>
          </div>
          <div className={styles.countItem}>
            <span className={styles.countLabel}>Total users</span>
            <span className={styles.countValue}>{counts.total_users}</span>
          </div>
        </div>
      )}

      {/* Stat Cards – single card with dividers */}
      {has('init_stats') && stats && (
        <div className={styles.statsCard}>
          {STAT_CARDS_META.map((meta) => {
            const stat = stats[meta.key];
            if (!stat) return null;
            const delta = stat.wow_delta;
            const isPositive = delta != null && delta > 0;
            const isNegative = delta != null && delta < 0;
            const ringColor = isPositive ? '#12B886' : isNegative ? '#FA5252' : '#4C6EF5';
            return (
              <div key={meta.key} className={styles.statSection}>
                <span className={styles.statLabel}>{meta.label}</span>
                <div className={styles.statBody}>
                  <RingProgress
                    size={60}
                    thickness={5}
                    roundCaps
                    sections={[{ value: stat.value, color: ringColor }]}
                    rootColor="#E9ECEF"
                    styles={{ root: { transform: 'scale(1.22)', transformOrigin: 'center' } }}
                    label={
                      <div className={styles.ringIcon} style={{ color: isNegative ? '#FA5252' : '#12B886' }}>
                        {isNegative ? (
                          <Icon iconName="BlackArrowDown" width="1rem" height="1rem" />
                        ) : (
                          <Icon iconName="BlackArrowUp" width="1rem" height="1rem" />
                        )}
                      </div>
                    }
                  />
                  <div className={styles.statValues}>
                    <span className={styles.statMainValue}>{meta.format(stat.value)}</span>
                    {delta != null && (
                      <div className={styles.statDelta}>
                        <span className={`${styles.deltaValue} ${isPositive ? styles.deltaUp : styles.deltaDown}`}>
                          {Math.abs(delta)}%
                        </span>
                        <span className={isPositive ? styles.deltaUp : styles.deltaDown}>
                          {isPositive ? (
                            <Icon iconName="GreenUpIcon" width="0.5rem" height="0.5rem" />
                          ) : (
                            <Icon iconName="IconDown" width="0.5rem" height="0.5rem" />
                          )}
                        </span>
                        <span className={styles.deltaText}>to previous week</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity Over Time */}
      {has('init_activity_over_time') && activity_over_time && (
        <div className={styles.chartCard}>
          <h3 className={styles.chartTitle}>Activity over time</h3>
          <LineChart
            h={250}
            data={activityChartData}
            dataKey="week"
            series={[
              { name: 'avg_days_logged', color: '#4C6EF5', label: 'Days logged' },
              { name: 'average', color: '#BE4BDB', label: 'Average engagement' },
            ]}
            curveType="linear"
            withDots
            dotProps={{ r: 4 }}
            gridAxis="y"
            yAxisProps={{ domain: [0, 'auto'] }}
            yAxisLabel="Avg. days logged"
            referenceLines={[
              {
                y: activity_over_time.all_time_average,
                color: '#BE4BDB',
                label: '',
              },
            ]}
          />
        </div>
      )}

      {/* Distribution + Positive Impacts side-by-side */}
      {(has('init_distribution') || has('init_positive_impacts')) && (
        <div className={styles.chartsRow}>
          {has('init_distribution') && distribution && (
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Activity last 7 days</h3>
              <BarChart
                h={250}
                data={distributionData}
                dataKey="bucket"
                series={[{ name: 'percentage', color: '#4C6EF5', label: '% of users' }]}
                gridAxis="y"
                yAxisProps={{ domain: [0, 100] }}
                yAxisLabel="% of users"
                xAxisLabel="Avg days a user has logged a habit"
              />
            </div>
          )}
          {has('init_positive_impacts') && top_positive_impacts && top_positive_impacts.length > 0 && (
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Top positive impacts last  7 days</h3>
              <BarChart
                h={250}
                data={impactsData}
                dataKey="name"
                series={[{ name: 'count', color: '#4C6EF5', label: 'Times impact logged' }]}
                orientation="vertical"
                gridAxis="x"
                xAxisLabel="Times impact logged"
                yAxisLabel="Impacts"
                yAxisProps={{ width: 100, tick: <ImpactYAxisTick /> }}
                withTooltip={false}
              />
            </div>
          )}
          {has('init_positive_impacts') && top_positive_impacts && top_positive_impacts.length === 0 && (
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Top positive impacts last 7 days</h3>
              <p className={styles.emptyText}>No positive impacts logged in the last 7 days</p>
            </div>
          )}
        </div>
      )}

      {/* Stream Summary Table */}
      {has('init_stream_summary') && streams && (
        <div className={styles.streamTable}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Stream</th>
                <th>Cohorts</th>
                <th>Behaviours</th>
                <th>Total users</th>
                <th>% active users</th>
                {showStreamChevron && <th />}
              </tr>
            </thead>
            <tbody>
              {streams.length === 0 ? (
                <tr>
                  <td colSpan={showStreamChevron ? 6 : 5} className={styles.emptyText}>
                    No streams with active cohorts
                  </td>
                </tr>
              ) : (
                streams.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.cohorts_count}</td>
                    <td>{row.behaviours_count}</td>
                    <td>{row.total_users}</td>
                    <td>{row.active_users_pct}%</td>
                    {showStreamChevron && (
                      <td className={styles.chevronCell}>
                        <button
                          className={styles.chevronBtn}
                          onClick={() =>
                            setStreamModal({ programmeId: data.programme_id, streamId: row.id })
                          }
                        >
                          <Icon iconName="ArrowRightIcon" width="0.375rem" height="0.625rem" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stream Details Modal */}
      {streamModal && (
        <StreamDetailsModal
          programmeId={streamModal.programmeId}
          streamId={streamModal.streamId}
          onClose={() => setStreamModal(null)}
        />
      )}
    </div>
  );
};

export default InitiativeInsights;
